import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  CalendarCheck,
  ClipboardCheck,
  KeyRound,
  MapPin,
  ShieldCheck,
  Sparkles,
  ThumbsUp,
} from 'lucide-react';
import { useSiteData } from '../context/SiteDataContext';
import Reveal from '../components/Reveal';
import AreaCheck from '../components/AreaCheck';
import PackageCard from '../components/PackageCard';
import VehicleClassPicker from '../components/VehicleClassPicker';
import ServiceIcon from '../components/ServiceIcon';
import Stars from '../components/Stars';
import type { VehicleClass } from '../lib/types';
import { money } from '../lib/format';

export default function Home() {
  const { settings, packages, services, gallery, testimonials } = useSiteData();
  const [vehicleClass, setVehicleClass] = useState<VehicleClass>('car');

  const featured = [...packages].sort((a, b) => Number(b.popular) - Number(a.popular)).slice(0, 3);
  const featuredTestimonials = testimonials.filter((t) => t.featured).slice(0, 3);
  const shown = featuredTestimonials.length > 0 ? featuredTestimonials : testimonials.slice(0, 3);

  return (
    <div className="overflow-x-clip">
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative">
        <div className="hero-grid absolute inset-0" />
        <div className="hero-beam absolute inset-0" />
        <div className="absolute -top-40 left-1/2 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-accent-500/10 blur-[120px]" />

        <div className="container-x relative pb-16 pt-20 sm:pt-28">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal>
              <span className="glass inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-accent-300">
                <Sparkles size={13} /> {settings.tagline}
              </span>
            </Reveal>
            <Reveal delay={100}>
              <h1 className="heading-xl mt-6">
                {settings.heroTitle.split(' ').slice(0, -1).join(' ')}{' '}
                <span className="gradient-text">{settings.heroTitle.split(' ').slice(-1)}</span>
              </h1>
            </Reveal>
            <Reveal delay={200}>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-zinc-400">
                {settings.heroSubtitle}
              </p>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                <Link to="/booking" className="btn-primary text-base">
                  Book Your Detail <ArrowRight size={18} />
                </Link>
                <Link to="/services" className="btn-ghost text-base">
                  View Packages
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal delay={400}>
            <div className="relative mx-auto mt-14 max-w-5xl">
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-accent-600/40 via-sky-400/20 to-accent-600/40 blur-lg" />
              <div className="relative overflow-hidden rounded-3xl border border-white/10">
                <img
                  src={settings.heroImage}
                  alt="Freshly detailed vehicle with mirror-gloss paint"
                  className="h-[320px] w-full object-cover sm:h-[420px]"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-night-950/70 via-transparent to-transparent" />
              </div>
            </div>
          </Reveal>

          <Reveal delay={500}>
            <div className="mx-auto mt-10 grid max-w-4xl grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { icon: ShieldCheck, label: 'Fully Insured' },
                { icon: BadgeCheck, label: 'Premium Products' },
                { icon: ThumbsUp, label: 'Satisfaction Guaranteed' },
                { icon: MapPin, label: settings.travel.mode !== 'off' ? 'Mobile — We Come To You' : settings.serviceArea },
              ].map((s) => (
                <div key={s.label} className="glass flex items-center justify-center gap-2.5 rounded-xl px-4 py-3.5 text-center">
                  <s.icon size={18} className="shrink-0 text-accent-400" />
                  <span className="text-sm font-medium text-zinc-200">{s.label}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={600}>
            <div className="mt-10">
              <AreaCheck />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Packages preview ─────────────────────────────────── */}
      <section className="container-x py-20">
        <Reveal>
          <div className="text-center">
            <span className="eyebrow">Detailing Packages</span>
            <h2 className="heading-lg mt-3">Pick Your Level of Perfect</h2>
            <p className="mx-auto mt-4 max-w-xl text-zinc-400">
              Transparent pricing by vehicle size. No surprises, no upsell games — choose your
              vehicle type to see exact pricing.
            </p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-8 flex justify-center">
            <VehicleClassPicker value={vehicleClass} onChange={setVehicleClass} />
          </div>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {featured.map((pkg, i) => (
            <Reveal key={pkg.id} delay={i * 120}>
              <PackageCard pkg={pkg} vehicleClass={vehicleClass} compact />
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div className="mt-10 text-center">
            <Link to="/services" className="btn-ghost">
              Compare All Packages & Add-ons <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ── Process ──────────────────────────────────────────── */}
      <section className="border-y border-white/[0.06] bg-night-900/60 py-20">
        <div className="container-x">
          <Reveal>
            <div className="text-center">
              <span className="eyebrow">How It Works</span>
              <h2 className="heading-lg mt-3">Booking Takes 60 Seconds</h2>
            </div>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: CalendarCheck, title: 'Book Online', text: 'Pick your package, vehicle, and a time that works. No payment needed to book.' },
              { icon: ClipboardCheck, title: 'We Confirm', text: 'We review your request and confirm your appointment by text or email, usually within hours.' },
              { icon: Sparkles, title: 'We Work Our Magic', text: 'Documented, methodical detailing with professional products — no shortcuts.' },
              { icon: KeyRound, title: 'Drive Off Impressed', text: 'Walk-around inspection together, aftercare tips, and pay however you like.' },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 100}>
                <div className="card glass-hover relative h-full p-6">
                  <span className="absolute right-5 top-4 font-display text-5xl font-extrabold text-white/[0.06]">
                    {i + 1}
                  </span>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-accent-600 to-sky-500 text-white">
                    <s.icon size={22} />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Custom work teaser ───────────────────────────────── */}
      <section className="container-x py-20">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="eyebrow">Beyond Detailing</span>
              <h2 className="heading-lg mt-3">Custom Automotive Work</h2>
              <p className="mt-3 max-w-xl text-zinc-400">
                Detailing is just the start. Audio, lighting, wraps, and protection — built out by
                the same shop that obsesses over your paint.
              </p>
            </div>
            <Link to="/custom" className="btn-ghost">
              Explore Custom Work <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {services.slice(0, 4).map((svc, i) => (
            <Reveal key={svc.id} delay={i * 100}>
              <Link to="/custom" className="card glass-hover block h-full p-6">
                <div className="flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/[0.06] text-accent-400">
                    <ServiceIcon name={svc.icon} size={22} />
                  </div>
                  {svc.comingSoon && (
                    <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-300">
                      Coming Soon
                    </span>
                  )}
                </div>
                <h3 className="mt-4 font-display text-lg font-bold text-white">{svc.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{svc.description}</p>
                {svc.priceFrom != null && (
                  <p className="mt-3 text-sm font-semibold text-accent-400">From {money(svc.priceFrom)}</p>
                )}
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── Gallery preview ──────────────────────────────────── */}
      <section className="border-y border-white/[0.06] bg-night-900/60 py-20">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <span className="eyebrow">Our Work</span>
                <h2 className="heading-lg mt-3">Results That Speak For Themselves</h2>
              </div>
              <Link to="/gallery" className="btn-ghost">
                Full Gallery <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3">
            {gallery.slice(0, 6).map((g, i) => (
              <Reveal key={g.id} delay={(i % 3) * 100}>
                <Link to="/gallery" className="group relative block overflow-hidden rounded-2xl border border-white/10">
                  <img
                    src={g.url}
                    alt={g.caption}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-end bg-gradient-to-t from-night-950/80 via-transparent to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <span className="text-sm font-medium text-white">{g.caption}</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────── */}
      {shown.length > 0 && (
      <section className="container-x py-20">
        <Reveal>
          <div className="text-center">
            <span className="eyebrow">Reviews</span>
            <h2 className="heading-lg mt-3">Trusted By People Who Love Their Cars</h2>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {shown.map((t, i) => (
            <Reveal key={t.id} delay={i * 120}>
              <figure className="card glass-hover flex h-full flex-col p-6">
                <Stars rating={t.rating} />
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-zinc-300">
                  “{t.text}”
                </blockquote>
                <figcaption className="mt-5 border-t border-white/[0.06] pt-4">
                  <span className="font-display font-semibold text-white">{t.name}</span>
                  {t.vehicle && <span className="ml-2 text-xs text-zinc-500">{t.vehicle}</span>}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>
      )}

      {/* ── About / CTA ──────────────────────────────────────── */}
      <section className="container-x pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-night-800 to-night-900 p-10 sm:p-14">
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-accent-500/15 blur-[90px]" />
            <div className="relative grid items-center gap-10 lg:grid-cols-2">
              <div>
                <span className="eyebrow">Why Apex</span>
                <h2 className="heading-lg mt-3">One Shop. One Standard.</h2>
                <p className="mt-4 leading-relaxed text-zinc-400">{settings.aboutText}</p>
              </div>
              <div className="flex flex-col items-start gap-4 lg:items-end">
                <p className="font-display text-2xl font-bold text-white lg:text-right">
                  Ready to see your car <span className="gradient-text">at its peak?</span>
                </p>
                <Link to="/booking" className="btn-primary text-base">
                  Book Your Appointment <ArrowRight size={18} />
                </Link>
                <span className="text-sm text-zinc-500">No payment required to book.</span>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
